/**
 * MyCopro - Notification Store
 * Gestion des notifications
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  Notification, 
  NotificationPreferences,
  DEFAULT_NOTIFICATION_PREFERENCES,
  UserCategoryType,
  filterNotificationsForUser,
  countUnreadNotifications,
  sortNotificationsByDate
} from '../types';

// ============================================================================
// TYPES DU STORE
// ============================================================================

interface NotificationState {
  // Liste des notifications
  notifications: Notification[];
  
  // Préférences de notifications
  preferences: NotificationPreferences;
  
  // État de chargement
  isLoading: boolean;
  error: string | null;
}

interface NotificationActions {
  // Chargement
  setNotifications: (notifications: Notification[]) => void;
  setPreferences: (preferences: NotificationPreferences) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // CRUD
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  
  // Marquer comme lu
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  
  // Préférences
  updatePreference: (key: keyof NotificationPreferences, value: boolean) => void;
  resetPreferences: () => void;
  
  // Reset
  reset: () => void;
}

type NotificationStore = NotificationState & NotificationActions;

// ============================================================================
// ÉTAT INITIAL
// ============================================================================

const initialState: NotificationState = {
  notifications: [],
  preferences: DEFAULT_NOTIFICATION_PREFERENCES,
  isLoading: false,
  error: null
};

// ============================================================================
// STORE
// ============================================================================

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ========================================
      // CHARGEMENT
      // ========================================
      
      setNotifications: (notifications) => {
        set({ notifications, isLoading: false, error: null });
      },

      setPreferences: (preferences) => {
        set({ preferences });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },

      // ========================================
      // CRUD
      // ========================================
      
      addNotification: (notification) => {
        set(state => ({
          notifications: [notification, ...state.notifications]
        }));
      },

      removeNotification: (id) => {
        set(state => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }));
      },

      // ========================================
      // MARQUER COMME LU
      // ========================================
      
      markAsRead: (id) => {
        set(state => ({
          notifications: state.notifications.map(n => 
            n.id === id ? { ...n, read: true } : n
          )
        }));
      },

      markAllAsRead: () => {
        set(state => ({
          notifications: state.notifications.map(n => ({ ...n, read: true }))
        }));
      },

      // ========================================
      // PRÉFÉRENCES
      // ========================================
      
      updatePreference: (key, value) => {
        set(state => ({
          preferences: { ...state.preferences, [key]: value }
        }));
      },

      resetPreferences: () => {
        set({ preferences: DEFAULT_NOTIFICATION_PREFERENCES });
      },

      // ========================================
      // RESET
      // ========================================
      
      reset: () => {
        set(initialState);
      }
    }),
    {
      name: 'mycopro-notifications',
      partialize: (state) => ({
        preferences: state.preferences
      })
    }
  )
);

// ============================================================================
// SÉLECTEURS
// ============================================================================

export const selectNotifications = (state: NotificationStore) => state.notifications;
export const selectPreferences = (state: NotificationStore) => state.preferences;
export const selectIsLoading = (state: NotificationStore) => state.isLoading;
export const selectError = (state: NotificationStore) => state.error;

// Notifications triées (plus récentes en premier)
export const selectSortedNotifications = (state: NotificationStore): Notification[] => {
  return sortNotificationsByDate(state.notifications);
};

// Sélecteur avec permissions utilisateur
export const selectNotificationsForUser = (
  userCategory: UserCategoryType
) => (state: NotificationStore): Notification[] => {
  const filtered = filterNotificationsForUser(state.notifications, userCategory);
  return sortNotificationsByDate(filtered);
};

// Notifications non lues
export const selectUnreadNotifications = (state: NotificationStore): Notification[] => {
  return state.notifications.filter(n => !n.read);
};

// Notifications non lues pour utilisateur
export const selectUnreadNotificationsForUser = (
  userCategory: UserCategoryType
) => (state: NotificationStore): Notification[] => {
  const filtered = filterNotificationsForUser(state.notifications, userCategory);
  return filtered.filter(n => !n.read);
};

// Comptage des non lues
export const selectUnreadCount = (state: NotificationStore): number => {
  return state.notifications.filter(n => !n.read).length;
};

// Comptage des non lues pour utilisateur
export const selectUnreadCountForUser = (
  userCategory: UserCategoryType
) => (state: NotificationStore): number => {
  return countUnreadNotifications(state.notifications, userCategory);
};

// Sélecteur par ID
export const selectNotificationById = (id: string) => (state: NotificationStore): Notification | undefined => {
  return state.notifications.find(n => n.id === id);
};

// Sélecteur par type
export const selectNotificationsByType = (type: string) => (state: NotificationStore): Notification[] => {
  return sortNotificationsByDate(
    state.notifications.filter(n => n.type === type)
  );
};

// Notifications récentes (dernières 24h)
export const selectRecentNotifications = (state: NotificationStore): Notification[] => {
  const oneDayAgo = new Date();
  oneDayAgo.setDate(oneDayAgo.getDate() - 1);
  
  return sortNotificationsByDate(
    state.notifications.filter(n => new Date(n.createdAt) > oneDayAgo)
  );
};

// Préférences individuelles
export const selectPreferenceMessages = (state: NotificationStore): boolean => 
  state.preferences.messages;

export const selectPreferenceEvents = (state: NotificationStore): boolean => 
  state.preferences.events;

export const selectPreferenceRequests = (state: NotificationStore): boolean => 
  state.preferences.requests;

export const selectPreferenceDocuments = (state: NotificationStore): boolean => 
  state.preferences.documents;

export const selectPreferenceAnnouncements = (state: NotificationStore): boolean => 
  state.preferences.announcements;

export const selectPreferenceEmailDigest = (state: NotificationStore): boolean => 
  state.preferences.emailDigest;
